import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import api, { formatApiError } from "../lib/api";
import { formatMoney, currencySymbol } from "../lib/money";
import { toast } from "sonner";
import {
  Coins, Sparkles, ShieldCheck, Check, ArrowLeft, Star, Globe, IndianRupee, Gift,
  Clock, CheckCircle2, Users,
} from "lucide-react";

/**
 * Diploks Top-Up — pack picker + currency + entity-aware checkout.
 *
 * Currency routing (handled by the backend, mirrored in the UI banner):
 *   INR   → Factskoot Eduwellness Private Limited · Razorpay · GST
 *   foreign → Destineze Educational Voyages FZCO · Stripe · UAE VAT 0
 *
 * Acceptance gates (per spec):
 *   • Show pack, currency, billing entity, tax, total, bonus, expiry, terms
 *   • Require both Terms AND Diploks platform-credit notice acceptance
 *   • Generate invoice on success
 */
const INTL_CURRENCIES = ["USD", "AED", "EUR", "GBP", "HKD"];

const ENTITY_BY_CURRENCY = {
  INR: {
    entity: "Factskoot Eduwellness Private Limited",
    gateway: "Razorpay",
    note: "GST applicable",
    region: "India",
  },
  default_foreign: {
    entity: "Destineze Educational Voyages FZCO",
    gateway: "Stripe",
    note: "UAE VAT not charged currently",
    region: "International",
  },
};

export default function DiploksTopUpPage() {
  const navigate = useNavigate();
  const [packs, setPacks] = useState([]);
  const [currency, setCurrency] = useState("INR");
  const [geo, setGeo] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedTier, setSelectedTier] = useState(null);
  const [accept_terms, setAcceptTerms] = useState(false);
  const [accept_notice, setAcceptNotice] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Pull geo to default currency (HK → HKD, etc.). Falls back to INR.
  useEffect(() => {
    api.get("/payments/pricing-geo")
      .then((r) => {
        setGeo(r.data?.geo);
        const region = r.data?.geo?.region;
        const primary = r.data?.geo?.primary_currency;
        if (region === "international" && primary) {
          setCurrency(primary);
        }
      })
      .catch(() => { /* keep INR default */ });
    // Pull campaign STATUS list (eligible / already_applied / ended) so the
    // ribbon shows the right state (seats-left countdown / already-applied
    // confirmation / offer-ended notice).
    api.get("/diploks/wallet")
      .then((r) => setCampaigns(r.data?.campaign_status || []))
      .catch(() => setCampaigns([]));
  }, []);

  // Fetch packs whenever currency changes.
  useEffect(() => {
    setLoading(true);
    api.get(`/diploks/packs?currency=${currency}`)
      .then((r) => {
        setPacks(r.data?.packs || []);
        if (!selectedTier && (r.data?.packs || []).length) {
          // Default-select the "popular" pack if present.
          const pop = r.data.packs.find((p) => p.popular) || r.data.packs[1] || r.data.packs[0];
          setSelectedTier(pop.tier_key);
        }
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [currency, selectedTier]);

  const allowedCurrencies = useMemo(() => {
    if (!geo) return ["INR", ...INTL_CURRENCIES];
    if (geo.region === "india") return ["INR"];
    if (geo.region === "international") return INTL_CURRENCIES;
    return ["INR", ...INTL_CURRENCIES];
  }, [geo]);

  const selected = packs.find((p) => p.tier_key === selectedTier);
  const entity = currency === "INR" ? ENTITY_BY_CURRENCY.INR : ENTITY_BY_CURRENCY.default_foreign;

  const handleBuy = async () => {
    if (!selected) {
      toast.error("Please pick a Diploks pack");
      return;
    }
    if (!accept_terms || !accept_notice) {
      toast.error("Please accept the terms and the Diploks platform-credit notice to continue");
      return;
    }
    setSubmitting(true);
    try {
      const body = {
        tier_key: selectedTier,
        currency,
        accept_terms,
        accept_diploks_notice: accept_notice,
        success_url: `${window.location.origin}/dashboard/diploks/wallet?topup={CHECKOUT_SESSION_ID}`,
        cancel_url: `${window.location.origin}/dashboard/diploks/topup`,
      };
      const { data } = await api.post("/diploks/topup/start", body);
      if (data.gateway === "stripe") {
        if (data.checkout_url) {
          window.location.assign(data.checkout_url);
          return;
        }
        // Mock mode — confirm directly.
        const r = await api.post("/diploks/topup/confirm", { payment_transaction_id: data.payment_transaction_id });
        toast.success(`+${r.data.diploks_added} Diploks credited (mock mode)`);
        navigate("/dashboard/diploks/wallet");
        return;
      }
      if (data.gateway === "razorpay") {
        if (data.mode === "mock") {
          // Mock-confirm path for preview when live keys are absent.
          const r = await api.post("/diploks/topup/confirm", {
            payment_transaction_id: data.payment_transaction_id,
            razorpay_order_id: data.razorpay_order_id,
            razorpay_payment_id: "pay_mock_" + Math.random().toString(36).slice(2, 14),
            razorpay_signature: "mock_signature_for_dev_only",
          });
          toast.success(`+${r.data.diploks_added} Diploks credited (mock mode)`);
          navigate("/dashboard/diploks/wallet");
          return;
        }
        // Live Razorpay flow
        await loadRazorpay();
        const rzp = new window.Razorpay({
          key: data.razorpay_key_id,
          amount: data.amount_minor,
          currency: data.currency,
          name: "Diplomexa Diploks",
          description: `${selected.label} · ${selected.total_diploks} Diploks`,
          order_id: data.razorpay_order_id,
          handler: async (resp) => {
            try {
              const r = await api.post("/diploks/topup/confirm", {
                payment_transaction_id: data.payment_transaction_id,
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              });
              toast.success(`+${r.data.diploks_added} Diploks credited`);
              navigate("/dashboard/diploks/wallet");
            } catch (e) {
              toast.error(formatApiError(e));
            }
          },
          theme: { color: "#0A192F" },
        });
        rzp.open();
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout title="Top up Diploks">
      <div className="space-y-6" data-testid="diploks-topup-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Link
            to="/dashboard/diploks/wallet"
            className="inline-flex items-center text-xs text-slate-500 hover:text-[#0A192F]"
          >
            <ArrowLeft className="h-3 w-3 mr-1" /> Back to wallet
          </Link>
        </div>

        <div className="rounded-2xl bg-gradient-to-br from-[#0A192F] to-[#0F4C81] text-white p-6 md:p-7 border border-[#D4AF37]/30">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold flex items-center gap-1.5">
            <Sparkles className="h-3 w-3" /> Power up your conference
          </p>
          <h1 className="mt-2 text-2xl md:text-3xl font-bold">Top up Diploks</h1>
          <p className="text-sm text-slate-300 mt-1 max-w-2xl">
            Diploks unlock more delegates, AI reports, premium certificates,
            communication campaigns and advanced operations. Bigger packs come
            with bonus Diploks.
          </p>
        </div>

        {/* Founding Organiser (and other) bonus campaign ribbons */}
        {campaigns.length > 0 && (
          <div className="space-y-2" data-testid="diploks-campaign-ribbons">
            {campaigns.map((c) => (
              <CampaignRibbon key={c.campaign_id || c.id} campaign={c} selected={selected} />
            ))}
          </div>
        )}

        {/* Currency switcher */}
        <div className="flex flex-wrap items-center gap-2" data-testid="diploks-currency-switcher">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Currency</span>
          {allowedCurrencies.includes("INR") && (
            <button
              type="button"
              onClick={() => setCurrency("INR")}
              data-testid="diploks-currency-INR"
              className={`px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 ${
                currency === "INR" ? "bg-[#0A192F] text-white" : "bg-slate-100 text-slate-700"
              }`}
            >
              <IndianRupee className="h-3 w-3" /> INR
            </button>
          )}
          {allowedCurrencies.filter((c) => c !== "INR").map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCurrency(c)}
              data-testid={`diploks-currency-${c}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 ${
                currency === c ? "bg-[#0A192F] text-white" : "bg-slate-100 text-slate-700"
              }`}
            >
              <Globe className="h-3 w-3" /> {currencySymbol(c)} {c}
            </button>
          ))}
        </div>

        {/* Pack cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="diploks-pack-grid">
          {loading && (
            <p className="col-span-full text-sm text-slate-500 text-center py-8">Loading packs…</p>
          )}
          {!loading && packs.length === 0 && (
            <p className="col-span-full text-sm text-slate-500 text-center py-8">
              No packs available for {currency}.
            </p>
          )}
          {packs.map((p) => {
            const selectedFlag = p.tier_key === selectedTier;
            return (
              <button
                key={p.tier_key}
                type="button"
                onClick={() => setSelectedTier(p.tier_key)}
                data-testid={`diploks-pack-${p.tier_key}`}
                className={`relative text-left rounded-xl border p-4 transition-all ${
                  selectedFlag
                    ? "border-[#D4AF37] bg-[#D4AF37]/10 ring-2 ring-[#D4AF37]/30"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
              >
                {p.popular && (
                  <span className="absolute -top-2 left-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#0A192F] text-[#D4AF37] px-2 py-0.5 rounded-full">
                    <Star className="h-2.5 w-2.5 fill-current" /> Most popular
                  </span>
                )}
                <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                  {p.label}
                </p>
                <p className="mt-2 text-3xl font-bold text-[#0A192F]">
                  {p.diploks.toLocaleString()}
                </p>
                <p className="text-[11px] text-slate-500">Diploks</p>
                {p.bonus > 0 && (
                  <p className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2 py-0.5">
                    + {p.bonus} Bonus Diploks
                  </p>
                )}
                <p className="mt-3 pt-3 border-t border-slate-100 text-lg font-bold text-[#0A192F]">
                  {formatMoney(p.price, p.currency)}
                </p>
              </button>
            );
          })}
        </div>

        {/* Summary + entity banner + checkout */}
        {selected && (
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-5"
                 data-testid="diploks-checkout-summary">
              <p className="text-sm font-bold text-[#0A192F]">Order summary</p>
              <div className="mt-4 divide-y divide-slate-100 text-sm">
                <Row label={selected.label} value={`${selected.diploks.toLocaleString()} Diploks`} />
                {selected.bonus > 0 && (
                  <Row label="Bonus Diploks" value={`+ ${selected.bonus.toLocaleString()}`} accent="emerald" />
                )}
                <Row label="Total Diploks" value={`${selected.total_diploks.toLocaleString()}`} bold />
                <Row label="Price" value={formatMoney(selected.price, currency)} />
                <Row label="Tax"
                     value={currency === "INR" ? "GST applicable as configured" : "UAE VAT — Not charged"} />
                <Row label="Validity"
                     value={`Purchased Diploks: ~12 months. Bonus: ~6 months.`} />
                <Row label="Total payable" value={formatMoney(selected.price, currency)} bold />
              </div>

              {/* Entity banner */}
              <div className="mt-5 rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs flex items-start gap-2"
                   data-testid="diploks-billing-entity-banner">
                <ShieldCheck className="h-4 w-4 text-[#0F4C81] mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-[#0A192F]">
                    Billed by {entity.entity}
                  </p>
                  <p className="text-slate-600 mt-0.5">
                    Payment processed by {entity.gateway} · {entity.note}.
                  </p>
                </div>
              </div>

              {/* Compliance acceptance */}
              <div className="mt-5 space-y-2 text-xs text-slate-700">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={accept_terms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    data-testid="diploks-accept-terms"
                    className="mt-0.5"
                  />
                  <span>
                    I have read and accept the{" "}
                    <Link to="/policies/terms" className="text-[#0F4C81] underline">Terms</Link>{" "}
                    and{" "}
                    <Link to="/policies/refund" className="text-[#0F4C81] underline">Refund Policy</Link>.
                  </span>
                </label>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={accept_notice}
                    onChange={(e) => setAcceptNotice(e.target.checked)}
                    data-testid="diploks-accept-notice"
                    className="mt-0.5"
                  />
                  <span>
                    I understand that Diploks are Diplomexa platform credits used
                    to unlock eligible digital services. Diploks are not cash, not
                    cryptocurrency, not e-money, not a stored-value wallet, not an
                    investment product, not transferable, not withdrawable, not
                    resellable, and cannot be converted into money.
                  </span>
                </label>
              </div>

              <button
                type="button"
                onClick={handleBuy}
                disabled={submitting || !accept_terms || !accept_notice}
                data-testid="diploks-pay-now"
                className="mt-5 w-full py-3 rounded-lg bg-[#0A192F] text-white font-bold text-sm hover:bg-[#0F2C50] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              >
                {submitting ? "Processing…" : (
                  <>
                    <Coins className="h-4 w-4" />
                    Pay {formatMoney(selected.price, currency)} & get {selected.total_diploks.toLocaleString()} Diploks
                  </>
                )}
              </button>
            </div>

            {/* Side benefits */}
            <div className="space-y-3">
              <Benefit
                icon={Sparkles}
                title="What can I do with Diploks?"
                items={[
                  `${selected.total_diploks.toLocaleString()} delegate seats in any event`,
                  "AI Readiness · Position Paper · Resolution reviews",
                  "Premium certificates with QR verification",
                  "Email / WhatsApp / SMS campaign credits",
                  "Conference summary + chair feedback reports",
                ]}
              />
              <Benefit
                icon={ShieldCheck}
                title="Safe & transparent"
                items={[
                  "Every Diploks change is ledger-logged",
                  "Idempotent — no double-deductions",
                  "INR via Factskoot & Razorpay (live)",
                  "Foreign via Destineze & Stripe (live)",
                  "Refund policy applies on unused Diploks",
                ]}
              />
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function Row({ label, value, bold, accent }) {
  const accentCls = accent === "emerald" ? "text-emerald-700" : "text-[#0A192F]";
  return (
    <div className="flex justify-between py-2">
      <span className={`text-slate-600 ${bold ? "font-semibold" : ""}`}>{label}</span>
      <span className={`${accentCls} ${bold ? "font-bold" : "font-medium"}`}>{value}</span>
    </div>
  );
}

/**
 * Founding Organiser / bonus campaign ribbon — renders three distinct states:
 *   eligible        → gold-on-navy with live "X / N seats left" countdown
 *   already_applied → emerald confirmation strip with applied date
 *   ended           → muted amber "offer has ended" strip
 * The countdown is driven entirely by backend `campaign_status`; we never
 * hard-code scarcity numbers in the UI.
 */
function CampaignRibbon({ campaign: c, selected }) {
  const state = c.state || "eligible";
  const cap = c.cap || 0;
  const seatsLeft = c.seats_left ?? 0;
  const grantedCount = c.granted_count ?? 0;
  const computedBonus = selected
    ? Math.round((selected.diploks || 0) * (c.bonus_percent || 0) / 100) + (c.bonus_flat || 0)
    : 0;
  const testKey = c.campaign_key || c.key || "campaign";

  if (state === "ended") {
    return (
      <div
        data-testid={`diploks-campaign-ribbon-${testKey}`}
        data-state="ended"
        className="relative overflow-hidden rounded-xl border border-amber-200 bg-amber-50 text-amber-900 px-5 py-3 flex items-center gap-3"
      >
        <Clock className="h-5 w-5 text-amber-700 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold uppercase tracking-wider text-amber-800">
            {c.label}
          </p>
          <p className="text-sm mt-0.5" data-testid={`diploks-campaign-state-text-${testKey}`}>
            This founding offer has ended.
            {cap > 0 && (
              <span className="text-amber-700"> All {cap.toLocaleString()} seats were awarded.</span>
            )}
          </p>
        </div>
      </div>
    );
  }

  if (state === "already_applied") {
    return (
      <div
        data-testid={`diploks-campaign-ribbon-${testKey}`}
        data-state="already_applied"
        className="relative overflow-hidden rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-900 px-5 py-3 flex items-center gap-3"
      >
        <CheckCircle2 className="h-5 w-5 text-emerald-700 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold uppercase tracking-wider text-emerald-800">
            {c.label}
          </p>
          <p className="text-sm mt-0.5" data-testid={`diploks-campaign-state-text-${testKey}`}>
            Founding Organiser bonus already applied to your first top-up
            {c.applied_at && (
              <span className="text-emerald-700"> · {String(c.applied_at).slice(0, 10)}</span>
            )}
            .
          </p>
        </div>
      </div>
    );
  }

  // eligible
  return (
    <div
      data-testid={`diploks-campaign-ribbon-${testKey}`}
      data-state="eligible"
      className="relative overflow-hidden rounded-xl border border-[#D4AF37]/50 bg-gradient-to-r from-[#0A192F] via-[#0F2C50] to-[#0A192F] text-white px-5 py-3 flex items-center gap-3"
    >
      <Gift className="h-5 w-5 text-[#D4AF37] shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-bold text-[#D4AF37] uppercase tracking-wider">
          {c.label}
        </p>
        <p className="text-sm mt-0.5">{c.ribbon_text}</p>
        {cap > 0 && (
          <p
            className="text-[11px] text-[#D4AF37]/90 mt-1 inline-flex items-center gap-1 font-semibold"
            data-testid={`diploks-campaign-seats-left-${testKey}`}
          >
            <Users className="h-3 w-3" />
            {seatsLeft.toLocaleString()} / {cap.toLocaleString()} founding seats left
            <span className="text-slate-400 font-normal">
              ({grantedCount.toLocaleString()} claimed)
            </span>
          </p>
        )}
        {c.tagline && (
          <p className="text-[11px] text-slate-300 mt-0.5">{c.tagline}</p>
        )}
      </div>
      {computedBonus > 0 && (
        <div className="hidden sm:block text-right shrink-0">
          <p className="text-[10px] uppercase tracking-wider text-slate-300">You'll get</p>
          <p className="text-lg font-bold text-emerald-300">+{computedBonus} Bonus</p>
        </div>
      )}
    </div>
  );
}

function Benefit({ icon: Icon, title, items }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <p className="text-xs font-bold text-[#0A192F] flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5 text-[#D4AF37]" /> {title}
      </p>
      <ul className="mt-2 space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="text-[11px] text-slate-700 flex items-start gap-1.5">
            <Check className="h-3 w-3 text-emerald-600 mt-0.5 shrink-0" />
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
}

// Lazy-load Razorpay checkout script once.
let _rzpLoading = null;
function loadRazorpay() {
  if (window.Razorpay) return Promise.resolve();
  if (_rzpLoading) return _rzpLoading;
  _rzpLoading = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = resolve;
    s.onerror = reject;
    document.body.appendChild(s);
  });
  return _rzpLoading;
}
